'use client';

import { useState } from 'react';
import { PDFDocument } from 'pdf-lib';

type Classification =
  | 'individual'
  | 'c-corp'
  | 's-corp'
  | 'partnership'
  | 'trust'
  | 'llc'
  | 'other';
type TinType = 'ssn' | 'ein';

const P = 'topmostSubform[0].Page1[0].';
const B = P + 'Boxes3a-b_ReadOrder[0].';
const A = P + 'Address_ReadOrder[0].';
const CLASS_INDEX: Record<Classification, number> = {
  individual: 0,
  'c-corp': 1,
  's-corp': 2,
  partnership: 3,
  trust: 4,
  llc: 5,
  other: 6,
};
const digits = (s: string) => s.replace(/\D/g, '');

function triggerDownload(bytes: Uint8Array, name: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function BrowserW9Filler() {
  const [name, setName] = useState('');
  const [business, setBusiness] = useState('');
  const [classification, setClassification] = useState<Classification>('individual');
  const [llcLetter, setLlcLetter] = useState('');
  const [otherText, setOtherText] = useState('');
  const [address, setAddress] = useState('');
  const [cityStateZip, setCityStateZip] = useState('');
  const [exemptPayee, setExemptPayee] = useState('');
  const [fatca, setFatca] = useState('');
  const [account, setAccount] = useState('');
  const [tinType, setTinType] = useState<TinType>('ssn');
  const [tin, setTin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      if (!name.trim()) throw new Error('Line 1 (name) is required on a W-9.');
      const res = await fetch('/forms/fw9.pdf');
      if (!res.ok) throw new Error('Could not load the W-9 form. Please try again.');
      const pdf = await PDFDocument.load(await res.arrayBuffer());
      const form = pdf.getForm();
      const setText = (fq: string, v: string) => {
        if (!v) return;
        try {
          form.getTextField(fq).setText(v);
        } catch {
          /* field missing in this revision, skip */
        }
      };
      setText(P + 'f1_01[0]', name.trim());
      setText(P + 'f1_02[0]', business.trim());
      try {
        form.getCheckBox(B + `c1_1[${CLASS_INDEX[classification]}]`).check();
      } catch {
        /* skip */
      }
      if (classification === 'llc')
        setText(B + 'f1_03[0]', llcLetter.trim().slice(0, 1).toUpperCase());
      if (classification === 'other') setText(B + 'f1_04[0]', otherText.trim());
      setText(P + 'f1_05[0]', exemptPayee.trim());
      setText(P + 'f1_06[0]', fatca.trim());
      setText(A + 'f1_07[0]', address.trim());
      setText(A + 'f1_08[0]', cityStateZip.trim());
      setText(P + 'f1_10[0]', account.trim());
      const d = digits(tin);
      if (tinType === 'ssn' && d) {
        setText(P + 'f1_11[0]', d.slice(0, 3));
        setText(P + 'f1_12[0]', d.slice(3, 5));
        setText(P + 'f1_13[0]', d.slice(5, 9));
      } else if (tinType === 'ein' && d) {
        setText(P + 'f1_14[0]', d.slice(0, 2));
        setText(P + 'f1_15[0]', d.slice(2, 9));
      }
      try {
        form.flatten();
      } catch {
        /* leave fillable if flatten unsupported */
      }
      const bytes = Uint8Array.from(await pdf.save());
      triggerDownload(bytes, 'cleanor.app-form-w9-filled.pdf');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not fill the W-9.');
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--muted, #5b6072)',
  };
  const inputStyle: React.CSSProperties = { padding: '8px 10px' };

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Fillable W-9 form</h2>
          <p>
            Fill the official IRS Form W-9 and download it as a PDF. Everything runs in your
            browser, so your details, including your SSN, never leave your device.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div
        className="tool-live-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginTop: 8,
        }}
      >
        <label style={fieldStyle}>
          <span style={labelStyle}>1. Name (as on your tax return)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>2. Business name (if different)</span>
          <input
            value={business}
            onChange={(e) => setBusiness(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>3a. Federal tax classification</span>
          <select
            value={classification}
            onChange={(e) => setClassification(e.target.value as Classification)}
            style={inputStyle}
          >
            <option value="individual">Individual / sole proprietor</option>
            <option value="c-corp">C corporation</option>
            <option value="s-corp">S corporation</option>
            <option value="partnership">Partnership</option>
            <option value="trust">Trust / estate</option>
            <option value="llc">LLC</option>
            <option value="other">Other</option>
          </select>
        </label>
        {classification === 'llc' ? (
          <label style={fieldStyle}>
            <span style={labelStyle}>LLC tax classification (C, S or P)</span>
            <input
              value={llcLetter}
              maxLength={1}
              onChange={(e) => setLlcLetter(e.target.value)}
              style={inputStyle}
            />
          </label>
        ) : null}
        {classification === 'other' ? (
          <label style={fieldStyle}>
            <span style={labelStyle}>Other classification</span>
            <input
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              style={inputStyle}
            />
          </label>
        ) : null}
        <label style={fieldStyle}>
          <span style={labelStyle}>5. Address (number, street, apt.)</span>
          <input value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>6. City, state and ZIP code</span>
          <input
            value={cityStateZip}
            onChange={(e) => setCityStateZip(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>TIN type</span>
          <select
            value={tinType}
            onChange={(e) => setTinType(e.target.value as TinType)}
            style={inputStyle}
          >
            <option value="ssn">Social Security Number (SSN)</option>
            <option value="ein">Employer ID Number (EIN)</option>
          </select>
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>{tinType === 'ssn' ? 'SSN (9 digits)' : 'EIN (9 digits)'}</span>
          <input
            value={tin}
            onChange={(e) => setTin(e.target.value)}
            placeholder={tinType === 'ssn' ? '123-45-6789' : '12-3456789'}
            style={inputStyle}
          />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>4. Exempt payee code (optional)</span>
          <input
            value={exemptPayee}
            onChange={(e) => setExemptPayee(e.target.value)}
            style={inputStyle}
          />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>4. FATCA exemption code (optional)</span>
          <input value={fatca} onChange={(e) => setFatca(e.target.value)} style={inputStyle} />
        </label>
        <label style={fieldStyle}>
          <span style={labelStyle}>7. Account numbers (optional)</span>
          <input value={account} onChange={(e) => setAccount(e.target.value)} style={inputStyle} />
        </label>
      </div>

      <div className="hero-actions tool-live-actions" style={{ marginTop: 18 }}>
        <button className="button" type="button" onClick={generate} disabled={busy}>
          {busy ? 'Building PDF…' : 'Download filled W-9 PDF'}
        </button>
      </div>
      {error ? <p className="support-feedback support-feedback-error">{error}</p> : null}
      <p
        className="support-feedback"
        style={{ marginTop: 12, fontSize: 13, color: 'var(--muted, #5b6072)' }}
      >
        This fills the official IRS Form W-9. Sign and date the printed or downloaded form, give it
        to whoever requested it, and do not send it to the IRS. This tool does not provide tax
        advice.
      </p>
    </article>
  );
}
